import React from 'react'
import { createRoot } from 'react-dom/client'
import { HostEnvironment } from '@typesApp/Environment'
import { AppConfig, Widget, WidgetConfig, WidgetInstance } from '@typesApp/Widget'
import { normalizeBrand } from '@utils/normalizeBrand'
import { BasicWidgetApp, WidgetApp } from './WidgetApp'

export const createWidgetHolder = (
  AppElementType: React.ElementType,
  appId: string,
  fallbackSelector = '#insuranceApp'
): Widget<AppConfig> => {
  const widgetHolder: Widget<AppConfig> = {
    new: (config: AppConfig): WidgetInstance => {
      const selector: string = config?.selector ?? fallbackSelector
      const node = getRootNode(selector)
      const root = widgetHolder.roots[selector] ?? createRoot(node)
      if (!widgetHolder.roots[selector]) {
        widgetHolder.roots[selector] = root
      }

      initEnvProps(config?.environment)

      return {
        render: () => {
          root.render(
            <React.StrictMode>
              <WidgetApp appId={appId} brand={config?.environment?.brand} locale={config?.environment?.locale}>
                <AppElementType {...config} />
              </WidgetApp>
            </React.StrictMode>
          )
        },
      }
    },
    close: () => {
      unmount(widgetHolder)
    },
    roots: {},
  }

  return widgetHolder
}

const getRootNode = (selector: string): HTMLElement => {
  const node = document.querySelector<HTMLElement>(selector)
  if (!node) {
    throw Error(`Selector is not valid -- ${selector}`)
  }
  return node
}

/**
 * Make some config items available outside of hooks
 */
const initEnvProps = (props: HostEnvironment) => {
  const host = props?.host
  const brand = props?.brand
  const locale = props?.locale
  const currency = props?.currency
  const localEnv = props?.localEnv

  if (!host || !brand || !locale || !currency) {
    return
  }

  window.directBilling = {
    ...window.directBilling,
    env: {
      ...window.directBilling?.env,
      host: host,
      brand: normalizeBrand(brand),
      locale: locale,
      currency: currency,
      localEnv: localEnv,
    },
  }
}

const unmount = <T extends WidgetConfig>(widget: Widget<T>) => {
  if (!widget?.roots) return

  try {
    const selectors = Object.keys(widget.roots)
    selectors.forEach(selector => {
      const root = widget.roots[selector]
      root?.unmount()
    })
    widget.roots = {}
  } catch (error) {
    console.warn('Error unmounting widget', error)
  }
}

export const createBasicWidgetHolder = (
  AppElementType: React.ElementType,
  fallbackSelector = '#insuranceInitializerApp'
): Widget<WidgetConfig> => {
  const widgetHolder: Widget<WidgetConfig> = {
    new: (config: WidgetConfig): WidgetInstance => {
      const selector: string = config?.selector ?? fallbackSelector
      const node = getRootNode(selector)
      const root = widgetHolder.roots[selector] ?? createRoot(node)
      if (!widgetHolder.roots[selector]) {
        widgetHolder.roots[selector] = root
      }

      return {
        render: () => {
          root.render(
            <React.StrictMode>
              <BasicWidgetApp>
                <AppElementType {...config} />
              </BasicWidgetApp>
            </React.StrictMode>
          )
        },
      }
    },
    close: () => {
      unmount(widgetHolder)
    },
    roots: {},
  }

  return widgetHolder
}
